import { useState, type FormEvent } from 'react'
import { createFileRoute } from '@tanstack/react-router'
import { InboxIcon } from 'lucide-react'
import type { FormRouting, FormSubmission } from '@hedgehog/shared'
import { api } from '@/lib/api'
import { formatDateTime } from '@/components/app/format'
import { OrderError, TabGate } from '@/components/app/order'
import { FormError, LoadingState, errorMessage } from '@/components/app/states'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Spinner } from '@/components/ui/spinner'
import { Empty, EmptyDescription, EmptyHeader, EmptyMedia, EmptyTitle } from '@/components/ui/empty'

export const Route = createFileRoute('/o/$orderId/messages')({
  loader: async ({ params, abortController }) => {
    const base = `/orders/${encodeURIComponent(params.orderId)}`
    const [submissions, routing] = await Promise.all([
      api<FormSubmission[]>(`${base}/submissions`, { signal: abortController.signal }),
      api<FormRouting>(`${base}/form-routing`, { signal: abortController.signal }),
    ])
    return { submissions, routing }
  },
  pendingComponent: () => <LoadingState label="Loading messages" />,
  errorComponent: ({ error }) => (
    <TabGate tab="/o/$orderId/messages">
      <OrderError error={error} title="We could not load your messages" />
    </TabGate>
  ),
  component: () => (
    <TabGate tab="/o/$orderId/messages">
      <Messages />
    </TabGate>
  ),
})

function Messages() {
  const { submissions, routing } = Route.useLoaderData()
  const sorted = [...submissions].sort((a, b) => b.createdAt - a.createdAt)

  return (
    <div className="flex flex-col gap-8">
      {routing.enabled && <Recipients initial={routing} />}
      <MessageList sorted={sorted} />
    </div>
  )
}

/** Who gets each contact form message by email. */
function Recipients({ initial }: { initial: FormRouting }) {
  const { orderId } = Route.useParams()
  const [routing, setRouting] = useState(initial)
  const [text, setText] = useState(initial.custom ? initial.recipients.join(', ') : '')
  const [state, setState] = useState<'idle' | 'saving' | 'saved'>('idle')
  const [error, setError] = useState<string | null>(null)

  async function save(e: FormEvent) {
    e.preventDefault()
    const recipients = text.split(/[\s,;]+/).map((s) => s.trim()).filter(Boolean)
    if (recipients.length > routing.max) {
      setError(`Enter at most ${routing.max} addresses.`)
      return
    }
    setState('saving')
    setError(null)
    try {
      setRouting(await api<FormRouting>(`/orders/${encodeURIComponent(orderId)}/form-routing`, { method: 'PUT', json: { recipients } }))
      setState('saved')
    } catch (err) {
      setError(errorMessage(err))
      setState('idle')
    }
  }

  return (
    <section aria-labelledby="recipients" className="flex flex-col gap-3">
      <div className="flex flex-col gap-1">
        <h2 id="recipients" className="text-xl font-semibold">
          Who gets the messages
        </h2>
        <p className="text-sm text-muted-foreground">
          Each message is emailed to: <span className="font-medium text-foreground">{routing.recipients.join(', ') || 'nobody yet'}</span>.
        </p>
      </div>
      <form onSubmit={save} className="flex flex-col gap-2" noValidate>
        <Label htmlFor="recipients-input">Email addresses (up to {routing.max}, separated by commas)</Label>
        <div className="flex flex-col gap-2 sm:flex-row">
          <Input
            id="recipients-input"
            type="text"
            inputMode="email"
            autoComplete="off"
            placeholder="Leave empty to use your account email"
            value={text}
            onChange={(e) => {
              setText(e.target.value)
              setState('idle')
            }}
            aria-describedby="recipients-status"
          />
          <Button type="submit" className="sm:w-fit" disabled={state === 'saving'}>
            {state === 'saving' && <Spinner />}
            Save
          </Button>
        </div>
        <p id="recipients-status" role="status" className="text-sm text-muted-foreground">
          {state === 'saved' ? 'Saved.' : ''}
        </p>
        <FormError message={error} />
      </form>
    </section>
  )
}

function MessageList({ sorted }: { sorted: FormSubmission[] }) {
  return (
    <section aria-labelledby="messages" className="flex flex-col gap-4">
      <div className="flex flex-col gap-1">
        <h2 id="messages" className="text-xl font-semibold">
          Contact form messages
        </h2>
        <p className="text-sm text-muted-foreground">
          Messages sent through your site's contact form in the last 90 days. Each one was also emailed.
        </p>
      </div>
      {sorted.length === 0 ? (
        <Empty className="border border-border">
          <EmptyHeader>
            <EmptyMedia variant="icon">
              <InboxIcon />
            </EmptyMedia>
            <EmptyTitle>No messages yet</EmptyTitle>
            <EmptyDescription>When a visitor fills in your contact form, the message shows here.</EmptyDescription>
          </EmptyHeader>
        </Empty>
      ) : (
        <ul className="flex flex-col gap-3">
          {sorted.map((s) => (
            <li key={s.id} className="rounded-xl border border-border p-4">
              <p className="mb-3 text-sm text-muted-foreground">
                <time dateTime={new Date(s.createdAt).toISOString()}>{formatDateTime(s.createdAt)}</time>
              </p>
              <dl className="grid gap-x-4 gap-y-2 sm:grid-cols-[10rem_1fr]">
                {Object.entries(s.fields).map(([k, v]) => (
                  <div key={k} className="contents">
                    <dt className="text-sm font-medium capitalize">{k.replace(/[_-]+/g, ' ')}</dt>
                    <dd className="min-w-0 break-words whitespace-pre-wrap">{v || '—'}</dd>
                  </div>
                ))}
              </dl>
            </li>
          ))}
        </ul>
      )}
    </section>
  )
}
